(ns hai.admin-ui.users.contacts.card
  "Rolodex card view for the contacts collection."
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table-views]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private edit-icon (icons/mui-icon :outlined :edit))
(def ^:private trash-icon (icons/mui-icon :outlined :delete))

;; ── Card shell ────────────────────────────────────────────────────────────────

(o/defstyled rolodex-card card/card :article
  :relative :flex :flex-col
  {:background-color t/--colour-snow
   :border-radius "12px"
   :gap "8px"
   :border           "1px solid var(--colour-rock)"
   :transition       "box-shadow 0.2s, transform 0.15s"}
  [:&:hover {:box-shadow "0 4px 20px rgba(30,8,93,0.07)"}])

;; ── Layout primitives ─────────────────────────────────────────────────────────

(o/defstyled identity-row :div
  :flex :items-start {:gap "10px"})

(o/defstyled avatar-circle :div
  :flex :items-center :justify-center :flex-shrink-0
  {:width "36px"
   :height "36px"
   :border-radius "50%"
   :background-color t/--colour-chalk
   :color t/--text-secondary-50
   :font-size "12px"
   :font-weight "700"
   :letter-spacing "0.03em"
   :user-select "none"}
  [:svg {:width "18px" :height "18px" :flex-shrink "0"}])

(o/defstyled name-wrap :div
  :flex-1 :min-w-0
  {:font-size "15px" :font-weight "600" :color t/--text-secondary})

(o/defstyled field-row :div
  :flex :items-center {:gap "6px" :min-height "20px"}
  [:svg {:width "13px" :height "13px" :flex-shrink "0" :opacity "0.4"}]
  [:a
   [:svg {:opacity "0" :transition "opacity 0.15s"}]
   [:&:hover [:svg {:opacity "1"}]]])

(o/defstyled field-text :span
  {:font-size "12px"
   :color t/--text-secondary
   :opacity "0.65"
   :overflow "hidden"
   :text-overflow "ellipsis"
   :white-space "nowrap"
   :line-height "1.4"})

(o/defstyled tags-row :div
  :flex :flex-wrap {:gap "4px"})

(o/defstyled tag-chip :span
  {:display "inline-flex"
   :align-items "center"
   :font-size "11px"
   :font-weight "500"
   :padding "2px 8px"
   :border-radius "9999px"
   :background-color t/--colour-chalk
   :color t/--text-secondary
   :opacity "0.75"})

;; ── Helpers ───────────────────────────────────────────────────────────────────

(defn- org? [contact-type]
  (= (or (:db/ident contact-type) contact-type) :contact.type/organisation))

(defn- initials [full-name]
  (->> (str/split (or full-name "") #"\s+")
       (keep first)
       (take 2)
       (apply str)
       str/upper-case))

(defn- github-url [login]
  (str "https://github.com/" login))

(defn- linkedin-href [url]
  (if (str/starts-with? (str url) "http") url (str "https://" url)))

(defn- clean-tag-name [display-name]
  (str "#" (str/replace (or display-name "") #"^#+" "")))

;; ── Sub-components ────────────────────────────────────────────────────────────

(defn- contact-avatar [{:contact/keys [type full-name]}]
  (let [label (if (org? type) "Organisation" "Individual")]
    [avatar-circle {:title label}
     (if (org? type)
       [icons/outlined-groups]
       (if (seq full-name)
         (initials full-name)
         [icons/outlined-person]))]))

(defn- email-section [emails]
  (when (seq emails)
    [field-row
     [:span {:title "Email"} [icons/outlined-email]]
     [field-text [:a {:href (str "mailto:" (first emails))
                      :style {:margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
                  (str (first emails))
                  [icons/outlined-north_east]]]]))

(defn- github-section [github-login]
  (when (seq github-login)
    [field-row
     [:span {:title "GitHub"} [icons/github {:size 13}]]
     [:a {:href   (github-url github-login)
          :target "_blank"
          :style  {:font-size "12px" :color t/--text-secondary
                   :opacity "0.65" :text-decoration "none"
                   :margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
      github-login
      [icons/outlined-north_east]]]))

(defn- linkedin-section [linkedin-url]
  (when (seq linkedin-url)
    [field-row
     {:title "Open LinkedIn profile"}
     [:span [icons/outlined-linkedin]]
     [:a {:href   (linkedin-href linkedin-url)
          :target "_blank"
          :style  {:font-size "12px" :color t/--text-secondary
                   :opacity "0.65" :text-decoration "none"
                   :margin-right "auto" :display "inline-flex" :align-items "center" :gap "4px"}}
      (last (str/split linkedin-url #"/"))
      [icons/outlined-north_east]]]))

(defn- source-account-section [auto-filled-from-connection-email]
  (when (seq auto-filled-from-connection-email)
    [field-row
     [:span [icons/calendar]]
     [c/Tooltip {:content "Calendar account this contact was auto-added from"}
      [:span [field-text auto-filled-from-connection-email]]]]))

(defn- members-section [members]
  (when (seq members)
    (let [names (->> members
                     (map #(or (:contact/full-name %) "Unnamed"))
                     (str/join ", "))]
      [field-row
       [:span {:title "Members"} [icons/outlined-people]]
       [field-text names]])))

(defn- tags-section [tags]
  (when (seq tags)
    [tags-row
     (for [{:group/keys [uuid display-name]} tags]
       ^{:key uuid}
       [tag-chip (clean-tag-name display-name)])]))

(defn- delete-dialog [!open? table-id initial-value]
  (let [{:contact/keys [full-name]} initial-value]
    [c/AlertDialogRoot
     {:open @!open? :onOpenChange #(reset! !open? %)}
     [c/AlertDialogContent
      [c/AlertDialogTitle "Delete contact"]
      [c/AlertDialogDescription
       "Are you sure you want to delete " [:strong (or full-name "this contact")] "?"]
      [c/Flex {:justify "end" :gap "3" :mt "4"}
       [c/Button {:variant "soft" :color "gray" :on-click #(reset! !open? false)} "Cancel"]
       [c/Button {:variant "solid" :color "red"
                  :on-click (fn []
                              (reset! !open? false)
                              (>e [:resolve [(list 'contact/delete initial-value)]
                                   {:on-success-fx [[:dispatch [:table/resolve table-id]]]}]))}
        "Delete"]]]]))

(defn- card-menu [!delete-open? table-id initial-value]
  [:span {:on-click #(.stopPropagation %)}
   [delete-dialog !delete-open? table-id initial-value]
   [table-views/more-menu-cell*
    [["Edit"   (fn [_] (>e [:routing/navigate [:contacts/edit initial-value]])) edit-icon]
     ["Delete" (fn [_] (reset! !delete-open? true)) trash-icon]]]])

;; ── Public entry point ────────────────────────────────────────────────────────

(defn contact-card
  [collection {:form/keys [initial-value] :as item}]
  (r/with-let [!delete-open? (r/atom false)]
    (let [{:contact/keys [full-name email members tags
                          github-login linkedin-url
                          auto-filled-from-connection-email]} initial-value
          table-id (:table/id collection)]
      [rolodex-card
       [card/card-top-row
        [cards/card-select-checkbox collection item]
        [card-menu !delete-open? table-id initial-value]]
       [identity-row
        [contact-avatar initial-value]
        [name-wrap full-name]]
       [email-section email]
       [github-section github-login]
       [linkedin-section linkedin-url]
       [source-account-section auto-filled-from-connection-email]
       [members-section members]
       [tags-section tags]])))
